import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {keys} from 'lodash';

import {batchGetMeetingSettings, getAITemplates} from 'Api/ZCalendar';
import {virtualUserEmailToUid} from 'Utils';

const initialState = {
  loadingState: {}, // {email: boolean}
  meetingSettings: {}, // {email: MeetingSettings}
  templateLoadingState: {}, // {email: boolean}
  templates: {}, // {email: AITemplate[]}
};

export const fetchMeetingSettings = createAsyncThunk(
  'meetingSettings/fetchMeetingSettings',
  async ({userIds}, thunkAPI) => {
    const state = thunkAPI.getState();
    const {loadingState, meetingSettings} = state.meetingSettingsState;
    const ids = Array.isArray(userIds) ? userIds : [userIds];

    const queryIds = ids.filter((id) => !loadingState[id.toLowerCase()] && !meetingSettings[id.toLowerCase()]);

    if (queryIds.length === 0) {
      return {data: {}};
    }

    thunkAPI.dispatch(setLoadingIds({ids: queryIds}));
    const response = await batchGetMeetingSettings(queryIds);
    return response;
  }
);

export const fetchMeetingAITemplates = createAsyncThunk(
  'meetingSettings/fetchMeetingAITemplates',
  async ({language, userId}, thunkAPI) => {
    const state = thunkAPI.getState();
    const lowerUserId = userId.toLowerCase();
    const {templateLoadingState, templates} = state.meetingSettingsState;
    if (templateLoadingState[lowerUserId] || templates[lowerUserId]) {
      return;
    }

    thunkAPI.dispatch(setTemplateLoadingId({id: lowerUserId}));
    const response = await getAITemplates(language, lowerUserId);
    return response.templates;
  }
);

export const meetingSettingsStore = createSlice({
  name: 'meetingSettingsStore',
  initialState: initialState,
  reducers: {
    setLoadingIds: (state, action) => {
      const {ids} = action.payload;
      ids.forEach((id) => {
        state.loadingState[id.toLowerCase()] = true;
      });
    },
    setTemplateLoadingId: (state, action) => {
      const {id} = action.payload;
      state.templateLoadingState[id.toLowerCase()] = true;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMeetingSettings.fulfilled, (state, action) => {
      const settings = action.payload.data;
      keys(settings).forEach((id) => {
        const lowerId = id.toLowerCase();
        state.loadingState[lowerId] = false;
        if (!settings[id].error) {
          state.meetingSettings[lowerId] = settings[id];
        }
      });
    });
    builder.addCase(fetchMeetingAITemplates.fulfilled, (state, action) => {
      if (!action.payload) {
        return;
      }
      const templates = action.payload;
      const lowerUserId = action.meta.arg.userId.toLowerCase();
      state.templateLoadingState[lowerUserId] = false;
      state.templates[lowerUserId] = templates;
    });
    builder.addCase(fetchMeetingAITemplates.rejected, (state, action) => {
      const lowerId = action.meta.arg.userId.toLowerCase();
      state.templateLoadingState[lowerId] = false;
    });
  },
});

export const selectMeetingSettings = (state, email) => {
  const userId = virtualUserEmailToUid(email).toLowerCase();
  const meetingSettings = state.meetingSettingsState.meetingSettings[userId];
  const loading = state.meetingSettingsState.loadingState[userId];
  return [meetingSettings, loading];
};

export const selectMeetingAITemplates = (state, email) => {
  const userId = virtualUserEmailToUid(email).toLowerCase();
  return [
    state.meetingSettingsState.templates[userId] ?? [],
    state.meetingSettingsState.templateLoadingState[userId],
  ];
};
export const {setLoadingIds, setTemplateLoadingId} = meetingSettingsStore.actions;

export default meetingSettingsStore.reducer;
